import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  Checkbox,
  Alert,
  CircularProgress,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link,
  Divider,
  Chip
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import UploadIcon from '@mui/icons-material/Upload';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import BrushIcon from '@mui/icons-material/Brush';
import BadgeIcon from '@mui/icons-material/Badge';
import RateReviewIcon from '@mui/icons-material/RateReview';
import SecurityIcon from '@mui/icons-material/Security';
import { UseApi } from '../Hooks/UseApi';
import { UseAuthRoles } from '../Hooks/UseAuthRoles';
import SignatureCanvas from './SignatureCanvas';
import { clientTerms } from '../Legal/Client-Terms';
import CombinedLegalCompliance from '../Legal/CombinedLegalCompliance';

// Input Box Component for consistent styling
// MOBILE FIX: wrapper is full-width on xs, auto on sm+; TextField width matches
const InputBox = ({ value, onChange, width = '200px', placeholder = '', error = false, helperText = '' }) => {
  return (
    <Box sx={{ width: { xs: '100%', sm: 'auto' } }}>
      <TextField
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        variant="outlined"
        size="small"
        placeholder={placeholder}
        error={error}
        sx={{
          width: { xs: '100%', sm: width },
          '& .MuiOutlinedInput-input': {
            textAlign: 'center',
            fontSize: '14px',
            padding: '8px 12px'
          },
          '& .MuiOutlinedInput-root': {
            backgroundColor: error ? 'error.light' : 'action.hover',
            '&:hover': {
              backgroundColor: error ? 'error.light' : 'action.selected'
            },
            '&.Mui-focused': {
              backgroundColor: 'background.paper'
            }
          }
        }}
      />
      {helperText && (
        <Typography variant="caption" color={error ? 'error' : 'textSecondary'} sx={{ mt: 1, display: 'block' }}>
          {helperText}
        </Typography>
      )}
    </Box>
  );
};

// Professional Custom Form Renderer
const CustomFormRenderer = ({ template, onSubmissionComplete }) => {
  const api = UseApi();
  const apiRef = useRef(api);
  const { getSubscriptionLevel } = UseAuthRoles();
  const currentTier = getSubscriptionLevel();

  const [formData, setFormData] = useState({});
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [sendAftercareEmail, setSendAftercareEmail] = useState(false);
  const [aftercareInstructions, setAftercareInstructions] = useState('');
  const [signatureData, setSignatureData] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const [submitStatus, setSubmitStatus] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [loadingClients, setLoadingClients] = useState(true);

  // Legal compliance state
  const [legalCompliance, setLegalCompliance] = useState({
    allComplianceComplete: false
  });

  // Artist section states
  const [artistSignatureData, setArtistSignatureData] = useState('');
  const [artistNumber, setArtistNumber] = useState('');
  const [artistReviewConfirmed, setArtistReviewConfirmed] = useState(false);

  // ID Upload related states
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [useExistingImage, setUseExistingImage] = useState(false);
  const [clientHasIdImage, setClientHasIdImage] = useState(false);
  const [secureIdImageSrc, setSecureIdImageSrc] = useState(null);
  const [clientIdImageBlob, setClientIdImageBlob] = useState(null);
  const [imageConfirmed, setImageConfirmed] = useState(false);
  const [loadingExistingImage, setLoadingExistingImage] = useState(false);
  const [existingImageError, setExistingImageError] = useState(null);

  // Terms dialog state
  const [showTermsDialog, setShowTermsDialog] = useState(false);

  const hasAftercareAccess = ['standard', 'premium', 'starter_studio', 'growth_studio'].includes(currentTier);

  // Determine if form has artist section
  const hasArtistSection = template?.form_config?.elements?.some(
    el => el.type === 'artist_section_divider'
  ) || false;

  // Find artist section divider index
  const artistDividerIndex = template?.form_config?.elements?.findIndex(
    el => el.type === 'artist_section_divider'
  ) ?? -1;

  // Split elements into client and artist sections
  const clientElements = artistDividerIndex !== -1
    ? template?.form_config?.elements?.slice(0, artistDividerIndex) || []
    : template?.form_config?.elements?.filter(el => ![
        'artist_section_divider',
        'artist_number',
        'artist_review_checkbox',
        'artist_signature'
      ].includes(el.type)) || [];

  const artistElements = artistDividerIndex !== -1
    ? template?.form_config?.elements?.slice(artistDividerIndex) || []
    : template?.form_config?.elements?.filter(el => [
        'artist_section_divider',
        'artist_number',
        'artist_review_checkbox',
        'artist_signature'
      ].includes(el.type)) || [];

  // Handle legal compliance changes
  const handleLegalComplianceChange = useCallback((compliance) => {
    setLegalCompliance(prev => {
      if (prev.allComplianceComplete !== compliance.allComplianceComplete) {
        return compliance;
      }
      return prev;
    });
  }, []);

  // Load clients
  useEffect(() => {
    const fetchClients = async () => {
      setLoadingClients(true);
      try {
        const clientsData = await apiRef.current.fetchClients();
        if (Array.isArray(clientsData)) {
          setClients(clientsData);
        } else {
          console.warn('⚠️ CustomFormRenderer: fetchClients returned non-array data:', clientsData);
          setClients([]);
        }
      } catch (error) {
        console.error('❌ CustomFormRenderer: Error fetching clients:', error);
        setSubmitStatus('Unable to load clients. Please refresh the page.');
        setClients([]);
      } finally {
        setLoadingClients(false);
      }
    };

    fetchClients();
  }, []);

  // Auto-populate aftercare instructions
  useEffect(() => {
    if (template?.form_config?.elements) {
      const aftercareElement = template.form_config.elements.find(
        el => el.type === 'aftercare_instructions' && el.enabled !== false
      );
      
      if (aftercareElement?.instructions) {
        setAftercareInstructions(aftercareElement.instructions);
        setSendAftercareEmail(true);
      }
    }
  }, [template?.id]);

  // Reset states when client changes
  useEffect(() => {
    if (selectedClient) {
      setUseExistingImage(false);
      setClientHasIdImage(false);
      setSecureIdImageSrc(null);
      setClientIdImageBlob(null);
      setExistingImageError(null);
      setImageConfirmed(false);
      setSelectedFiles([]);
      setLegalCompliance({ allComplianceComplete: false });
      setArtistSignatureData('');
      setArtistNumber('');
      setArtistReviewConfirmed(false);
    }
  }, [selectedClient?.id]);

  // Clean up object URL
  useEffect(() => {
    return () => {
      if (secureIdImageSrc) {
        URL.revokeObjectURL(secureIdImageSrc);
      }
    };
  }, [secureIdImageSrc]);

  // Handle file selection
  const handleFileChange = useCallback((e) => {
    const files = Array.from(e.target.files);
    const maxSize = 5 * 1024 * 1024;

    const validFiles = files.filter((file) => {
      if (file.size > maxSize) {
        alert(`File ${file.name} exceeds the 5MB size limit and will be skipped.`);
        return false;
      }
      return true;
    });
    setSelectedFiles(validFiles);
    setUseExistingImage(false);
    setImageConfirmed(false);
  }, []);

  // Handle use existing image
  const handleUseExistingImage = useCallback((e) => {
    const useExisting = e.target.checked;
    setUseExistingImage(useExisting);
    
    if (useExisting) {
      setSelectedFiles([]);
    } else {
      if (secureIdImageSrc) {
        URL.revokeObjectURL(secureIdImageSrc);
        setSecureIdImageSrc(null);
      }
      setClientIdImageBlob(null);
      setImageConfirmed(false);
      setClientHasIdImage(false);
    }
  }, [secureIdImageSrc]);

  // Fetch client ID image
  const fetchClientIdImage = async () => {
    if (!selectedClient || !selectedClient.id) {
      return;
    }
    
    setLoadingExistingImage(true);
    setExistingImageError(null);
    
    try {
      const clientResponse = await apiRef.current.fetchWithAuth(`/clients/${selectedClient.id}/`);
      if (clientResponse.client_id_image_url) {
        try {
          const response = await apiRef.current.fetchWithAuth(
            `/clients/${selectedClient.id}/secure-id/`
          );
          if (response instanceof Blob) {
            const objectUrl = URL.createObjectURL(response);
            setSecureIdImageSrc(objectUrl);
            setClientIdImageBlob(response);
            setClientHasIdImage(true);
          } else if (response.image_url || response.url || response.presigned_url) {
            const presignedUrl = response.image_url || response.url || response.presigned_url;
            setSecureIdImageSrc(presignedUrl);
            setClientHasIdImage(true);
            try {
              const imgResponse = await fetch(presignedUrl);
              const blob = await imgResponse.blob();
              setClientIdImageBlob(blob);
            } catch (blobError) {
              console.warn('Could not fetch image blob from presigned URL:', blobError);
            }
          } else if (response.image_data) {
            const byteCharacters = atob(response.image_data);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: response.content_type || 'image/jpeg' });
            const objectUrl = URL.createObjectURL(blob);
            setSecureIdImageSrc(objectUrl);
            setClientIdImageBlob(blob);
            setClientHasIdImage(true);
          } else {
            setExistingImageError('Unable to load the ID image in the expected format.');
          }
        } catch (imageError) {
          console.error('❌ Error fetching secure ID image:', imageError);
          setExistingImageError('Unable to load the ID image. Please upload a new one.');
        }
      } else {
        setExistingImageError('No ID image on file for this client. Please upload one.');
        setClientHasIdImage(false);
      }
    } catch (error) {
      console.error('❌ Error fetching client data:', error);
      setExistingImageError('Unable to check for existing ID image.');
    } finally {
      setLoadingExistingImage(false);
    }
  };

  // Handle confirm image
  const handleConfirmImage = useCallback(() => {
    setImageConfirmed(true);
  }, []);

  // Handle input changes
  const handleInputChange = useCallback((name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [validationErrors]);

  // Handle signature change
  const handleSignatureChange = useCallback((signature, mode) => {
    setSignatureData(signature);
    if (validationErrors.signature) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.signature;
        return newErrors;
      });
    }
  }, [validationErrors]);

  // Handle artist signature change
  const handleArtistSignatureChange = useCallback((signature, mode) => {
    setArtistSignatureData(signature);
    if (validationErrors.artistSignature) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.artistSignature;
        return newErrors;
      });
    }
  }, [validationErrors]);

  // Handle form element signature changes
  const handleFormElementSignatureChange = useCallback((name, signature, mode) => {
    setFormData(prev => ({
      ...prev,
      [name]: signature
    }));
    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [validationErrors]);

  // Render terms content
  const renderTermsContent = useCallback(() => {
    return (
      <Box sx={{ p: 2 }}>
        {clientTerms.sections?.map((section, sectionIndex) => (
          <Box key={sectionIndex} sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main', mb: 2 }}>
              {section.title}
            </Typography>
            
            {section.subsections?.map((subsection, subIndex) => (
              <Box key={subIndex} sx={{ mb: 2 }}>
                {subsection.title && (
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    {subsection.title}
                  </Typography>
                )}
                
                {Array.isArray(subsection.content) ? (
                  subsection.content.map((paragraph, pIndex) => (
                    <Typography key={pIndex} variant="body2" paragraph>
                      {paragraph}
                    </Typography>
                  ))
                ) : subsection.content ? (
                  <Typography variant="body2" paragraph>
                    {subsection.content}
                  </Typography>
                ) : null}
                
                {subsection.list && Array.isArray(subsection.list) && (
                  <Box component="ul" sx={{ pl: 2, mb: 2 }}>
                    {subsection.list.map((item, itemIndex) => (
                      <Typography key={itemIndex} component="li" variant="body2" sx={{ mb: 0.5 }}>
                        {item}
                      </Typography>
                    ))}
                  </Box>
                )}
              </Box>
            ))}
            
            {section.list && Array.isArray(section.list) && (
              <Box component="ul" sx={{ pl: 2, mb: 2 }}>
                {section.list.map((item, itemIndex) => (
                  <Typography key={itemIndex} component="li" variant="body2" sx={{ mb: 0.5 }}>
                    {item}
                  </Typography>
                ))}
              </Box>
            )}
          </Box>
        ))}

        {clientTerms.lastUpdated && (
          <Box sx={{ mt: 4, pt: 2, borderTop: '1px solid #e0e0e0' }}>
            <Typography variant="caption" color="text.secondary">
              Last updated: {clientTerms.lastUpdated}
            </Typography>
          </Box>
        )}
      </Box>
    );
  }, []);

  // Handle submit with artist section support
  const handleSubmit = useCallback(async () => {
    setSubmitting(true);
    const errors = {};
    if (!selectedClient) {
      errors.client = 'Please select a client';
    }
    
    if (!signatureData) {
      errors.signature = 'Client signature is required';
    }
    
    if (!legalCompliance.allComplianceComplete) {
      errors.legal = 'Please complete all legal requirements';
    }
    
    if (template?.enable_id_upload) {
      if (!useExistingImage && selectedFiles.length === 0) {
        errors.id = 'Please either upload new ID images or use the existing ID image.';
      }
      if (useExistingImage && !imageConfirmed) {
        errors.id = 'Please confirm the existing ID image before submitting.';
      }
    }
    
    clientElements.forEach((element, index) => {
      const fieldName = element.name || `field_${index}`;
      const fieldValue = formData[fieldName];
      
      if (element.required) {
        if (element.type === 'terms_conditions' || element.type === 'terms_and_conditions') {
          if (!fieldValue) {
            errors[fieldName] = 'You must agree to the terms and conditions';
          }
        } else if (element.type === 'checkbox') {
          if (!fieldValue) {
            errors[fieldName] = `${element.label || element.question || 'This field'} is required`;
          }
        } else {
          if (!fieldValue || (typeof fieldValue === 'string' && fieldValue.trim() === '')) {
            errors[fieldName] = `${element.label || element.question || 'This field'} is required`;
          }
        }
      }
    });

    if (hasArtistSection) {
      const hasArtistNumberElement = artistElements.some(el => el.type === 'artist_number');
      if (hasArtistNumberElement) {
        const artistNumberElement = artistElements.find(el => el.type === 'artist_number');
        if (artistNumberElement?.required !== false && !artistNumber.trim()) {
          errors.artistNumber = 'Artist/License number is required';
        }
      }

      const hasArtistReviewElement = artistElements.some(el => el.type === 'artist_review_checkbox');
      if (hasArtistReviewElement && !artistReviewConfirmed) {
        errors.artistReview = 'Artist must confirm form review';
      }

      const hasArtistSignatureElement = artistElements.some(el => el.type === 'artist_signature');
      if (hasArtistSignatureElement && !artistSignatureData) {
        errors.artistSignature = 'Artist signature is required';
      }
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      setSubmitStatus('Please correct the errors above');
      setSubmitting(false);
      return;
    }

    try {
      const submissionData = {
        template_id: template.id,
        client_id: selectedClient.id,
        form_data: {
          ...formData,
          ...(hasArtistSection && {
            artist_number: artistNumber,
            artist_review_confirmed: artistReviewConfirmed,
            artist_signature_provided: !!artistSignatureData
          })
        },
        signature: signatureData,
        artist_signature: artistSignatureData || null,
        artist_number: artistNumber || null,
        artist_review_confirmed: artistReviewConfirmed,
        use_existing_image: useExistingImage && imageConfirmed,
        aftercare_instructions: sendAftercareEmail ? (
          aftercareInstructions || 
          template?.form_config?.elements?.find(el => el.type === 'aftercare_instructions')?.instructions ||
          'Standard aftercare instructions'
        ) : ''
      };

      const response = await apiRef.current.submitCustomForm(submissionData);

      if (response.success) {
        if (!useExistingImage && selectedFiles.length > 0) {
          let uploadSuccessCount = 0;
          let uploadErrors = [];
          
          try {
            for (let i = 0; i < selectedFiles.length; i++) {
              const file = selectedFiles[i];
              try {
                await apiRef.current.uploadCustomFormImage(response.submission_id, file);
                uploadSuccessCount++;
              } catch (uploadError) {
                console.error(`❌ Upload ${i + 1} failed:`, uploadError);
                uploadErrors.push(`File ${i + 1} (${file.name}): ${uploadError.message}`);
              }
            }
            
            if (uploadSuccessCount === selectedFiles.length) {
              setSubmitStatus('Form submitted successfully with ID images!');
            } else if (uploadSuccessCount > 0) {
              setSubmitStatus(`Form submitted! ${uploadSuccessCount}/${selectedFiles.length} images uploaded. Some uploads failed.`);
            } else {
              setSubmitStatus('Form submitted but all image uploads failed.');
            }
          } catch (overallUploadError) {
            console.error('🖼️ Overall upload process failed:', overallUploadError);
            setSubmitStatus('Form submitted but there was an error uploading ID images.');
          }
        } else {
          setSubmitStatus('Form submitted successfully!');
        }
        
        setFormData({});
        setSignatureData('');
        setArtistSignatureData('');
        setArtistNumber('');
        setArtistReviewConfirmed(false);
        setSelectedClient(null);
        setSelectedFiles([]);
        setUseExistingImage(false);
        setImageConfirmed(false);
        setClientHasIdImage(false);
        if (secureIdImageSrc) {
          URL.revokeObjectURL(secureIdImageSrc);
          setSecureIdImageSrc(null);
        }
        setClientIdImageBlob(null);
        setTermsAccepted(false);
        setSendAftercareEmail(false);
        setAftercareInstructions('');
        setLegalCompliance({ allComplianceComplete: false });
        
        if (onSubmissionComplete) {
          onSubmissionComplete(response.submission_id);
        }
      } else {
        console.error('❌ Form submission failed:', response);
        setSubmitStatus('Error submitting form. Please try again.');
      }
    } catch (error) {
      console.error('💥 Submission error:', error);
      setSubmitStatus('Error submitting form. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }, [
    selectedClient, signatureData, artistSignatureData, artistNumber, artistReviewConfirmed,
    formData, template, legalCompliance, hasArtistSection, artistElements, clientElements,
    useExistingImage, imageConfirmed, selectedFiles, sendAftercareEmail,
    aftercareInstructions, secureIdImageSrc, onSubmissionComplete
  ]);

  // Render form element (client section elements)
  const renderFormElement = useCallback((element, index) => {
    const name = element.name || `field_${index}`;
    const hasError = validationErrors[name];
    
    switch (element.type) {
      case 'static_text':
        return (
          <Box
            key={index}
            sx={{
              p: { xs: 2, sm: 3 },
              mb: 3,
              bgcolor: 'action.hover',
              border: 1, borderColor: 'divider',
              borderRadius: 1,
              fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif'
            }}
          >
            <Typography 
              variant="body1" 
              sx={{ 
                whiteSpace: 'pre-wrap',
                lineHeight: 1.6,
                fontSize: '14px',
                color: 'text.primary'
              }}
            >
              {element.content}
            </Typography>
          </Box>
        );
      
      case 'section_header':
        return (
          <Box key={index} sx={{ mb: 3, mt: index > 0 ? 4 : 0 }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: 'primary.main',
                borderBottom: 3, borderColor: 'primary.main',
                pb: 1,
                mb: 2,
                // MOBILE FIX: scale down h5 on small screens
                fontSize: { xs: '1.1rem', sm: '1.5rem' }
              }}
            >
              {element.content}
            </Typography>
          </Box>
        );
      
      case 'text_input':
        return (
          <Box key={index} sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label={element.label}
              placeholder={element.placeholder}
              required={element.required}
              value={formData[name] || ''}
              onChange={(e) => handleInputChange(name, e.target.value)}
              variant="outlined"
              error={!!hasError}
              helperText={hasError}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: 'primary.main',
                  },
                },
              }}
            />
          </Box>
        );
      
      case 'checkbox':
        return (
          <Box key={index} sx={{ mb: 3 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData[name] || false}
                  onChange={(e) => handleInputChange(name, e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Typography sx={{ fontSize: '14px' }}>
                  {element.label}
                  {element.required && <Box component="span" sx={{ color: '#d32f2f', ml: 0.5 }}>*</Box>}
                </Typography>
              }
            />
            {hasError && (
              <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                {hasError}
              </Typography>
            )}
          </Box>
        );
      
      case 'yes_no':
        return (
          <Box key={index} sx={{ mb: 3 }}>
            <Typography 
              component="label"
              sx={{ 
                display: 'block',
                mb: 1, 
                fontWeight: 600,
                color: 'text.primary',
                fontSize: '14px'
              }}
            >
              {element.question || element.label}
              {element.required && <Box component="span" sx={{ color: '#d32f2f', ml: 0.5 }}>*</Box>}
            </Typography>
            <FormControl component="fieldset" error={!!hasError}>
              <RadioGroup
                value={formData[name] || ''}
                onChange={(e) => handleInputChange(name, e.target.value)}
                row
              >
                <FormControlLabel
                  value="yes"
                  control={<Radio size="small" />}
                  label={<Typography sx={{ fontSize: '14px' }}>Yes</Typography>}
                />
                <FormControlLabel
                  value="no"
                  control={<Radio size="small" />}
                  label={<Typography sx={{ fontSize: '14px' }}>No</Typography>}
                />
              </RadioGroup>
              {hasError && (
                <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                  {hasError}
                </Typography>
              )}
            </FormControl>
          </Box>
        );

      case 'radio_group':
        return (
          <Box key={index} sx={{ mb: 3 }}>
            <Typography 
              component="label"
              sx={{ 
                display: 'block',
                mb: 1, 
                fontWeight: 600,
                color: 'text.primary',
                fontSize: '14px'
              }}
            >
              {element.question || element.label}
              {element.required && <Box component="span" sx={{ color: '#d32f2f', ml: 0.5 }}>*</Box>}
            </Typography>
            <FormControl component="fieldset" error={!!hasError}>
              <RadioGroup
                value={formData[name] || ''}
                onChange={(e) => handleInputChange(name, e.target.value)}
                row={element.layout === 'horizontal'}
              >
                {element.options?.map((option, optionIndex) => {
                  const optionValue = typeof option === 'string' ? option : option.value;
                  const optionLabel = typeof option === 'string' ? option : option.label;
                  
                  return (
                    <FormControlLabel
                      key={optionIndex}
                      value={optionValue}
                      control={<Radio size="small" />}
                      label={<Typography sx={{ fontSize: '14px' }}>{optionLabel}</Typography>}
                    />
                  );
                })}
              </RadioGroup>
              {hasError && (
                <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                  {hasError}
                </Typography>
              )}
            </FormControl>
          </Box>
        );

      case 'input_box':
        // MOBILE FIX: stack label below input on mobile, side-by-side on sm+
        return (
          <Box
            key={index}
            sx={{
              mb: 3,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: 2
            }}
          >
            <InputBox
              value={formData[name] || ''}
              onChange={(value) => handleInputChange(name, value)}
              width={element.width || '200px'}
              placeholder={element.placeholder || ''}
              error={!!hasError}
              helperText={hasError}
            />
            <Typography sx={{ fontSize: '14px' }}>
              {element.label}
              {element.required && <Box component="span" sx={{ color: '#d32f2f', ml: 0.5 }}>*</Box>}
            </Typography>
          </Box>
        );

      case 'signature':
        return (
          <Box key={index} sx={{ mb: 4 }}>
            <SignatureCanvas
              onSignatureChange={(signature, mode) => handleFormElementSignatureChange(name, signature, mode)}
              value={formData[name] || ''}
              required={element.required}
              error={!!hasError}
              helperText={hasError}
              disabled={!legalCompliance.allComplianceComplete}
            />
          </Box>
        );

      case 'aftercare_instructions':
        if (element.enabled === false) {
          return null;
        }
        
        return (
          <Box key={index} sx={{ mb: 3 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                Aftercare instructions will be automatically sent to your email after form submission.
              </Typography>
            </Alert>
            
            {element.instructions && (
              <Box sx={{ 
                mb: 2, 
                p: 2, 
                bgcolor: 'info.50', 
                border: 1, borderColor: 'divider', 
                borderRadius: 1 
              }}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                  Aftercare Instructions Preview
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    whiteSpace: 'pre-wrap', 
                    lineHeight: 1.6,
                    color: 'text.primary',
                    fontFamily: 'inherit'
                  }}
                >
                  {element.instructions}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', fontStyle: 'italic' }}>
                  These instructions will be sent to your email after form completion.
                </Typography>
              </Box>
            )}
            
            <FormControlLabel
              control={
                <Checkbox
                  checked={sendAftercareEmail}
                  onChange={(e) => setSendAftercareEmail(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Typography sx={{ fontSize: '14px' }}>
                  Send aftercare instructions to my email after submission
                </Typography>
              }
            />
          </Box>
        );

      default:
        return null;
    }
  }, [formData, validationErrors, handleInputChange, handleFormElementSignatureChange, legalCompliance, sendAftercareEmail]);

  // Render artist section elements
  const renderArtistElement = useCallback((element, index) => {
    switch (element.type) {
      case 'artist_section_divider':
        // MOBILE FIX: responsive horizontal padding
        return (
          <Box 
            key={index} 
            sx={{ 
              my: 4, 
              py: 3, 
              px: { xs: 2, sm: 4 },
              bgcolor: 'action.hover', 
              borderRadius: 2,
              border: '2px dashed', borderColor: 'primary.main',
              textAlign: 'center'
            }}
          >
            <Divider sx={{ mb: 2 }}>
              <Chip 
                icon={<BrushIcon />} 
                label={element.title || 'Artist Section'} 
                color="primary"
                sx={{ fontSize: '14px', fontWeight: 600, py: 2.5 }}
              />
            </Divider>
            {element.instructions && (
              <Typography variant="body2" color="text.secondary">
                {element.instructions}
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              The section below is to be completed by the artist
            </Typography>
          </Box>
        );

      case 'artist_number':
        return (
          <Box key={index} sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label={element.label || 'Artist/License Number'}
              placeholder={element.placeholder || 'Enter your certification/license number'}
              required={element.required !== false}
              value={artistNumber}
              onChange={(e) => {
                setArtistNumber(e.target.value);
                if (validationErrors.artistNumber) {
                  setValidationErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors.artistNumber;
                    return newErrors;
                  });
                }
              }}
              variant="outlined"
              error={!!validationErrors.artistNumber}
              helperText={validationErrors.artistNumber}
              InputProps={{
                startAdornment: <BadgeIcon sx={{ mr: 1, color: 'secondary.main' }} />
              }}
            />
          </Box>
        );

      case 'artist_review_checkbox':
        return (
          <Box 
            key={index} 
            sx={{ 
              mb: 3, 
              p: 2, 
              bgcolor: 'action.hover', 
              borderRadius: 1, 
              border: 1, borderColor: 'secondary.light' 
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={artistReviewConfirmed}
                  onChange={(e) => {
                    setArtistReviewConfirmed(e.target.checked);
                    if (validationErrors.artistReview) {
                      setValidationErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors.artistReview;
                        return newErrors;
                      });
                    }
                  }}
                  color="secondary"
                />
              }
              label={
                <Typography variant="body2" fontWeight={500}>
                  {element.label || 'I have reviewed this form and confirm all information is accurate'}
                  <span style={{ color: 'red' }}> *</span>
                </Typography>
              }
            />
            {validationErrors.artistReview && (
              <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1, ml: 4 }}>
                {validationErrors.artistReview}
              </Typography>
            )}
          </Box>
        );

      case 'artist_signature':
        return (
          <Box key={index} sx={{ mb: 4 }}>
            <Typography variant="subtitle1" fontWeight={600} color="secondary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BrushIcon /> Artist Signature
            </Typography>
            <SignatureCanvas
              onSignatureChange={handleArtistSignatureChange}
              value={artistSignatureData}
              disabled={!artistReviewConfirmed}
              required={true}
              error={!!validationErrors.artistSignature}
              helperText={
                validationErrors.artistSignature || (
                  artistReviewConfirmed
                    ? "Sign to confirm your review"
                    : "Please confirm you have reviewed the form above before signing"
                )
              }
            />
          </Box>
        );

      case 'static_text':
        return (
          <Box
            key={index}
            sx={{
              p: { xs: 2, sm: 3 },
              mb: 3,
              bgcolor: 'action.hover',
              border: 1, borderColor: 'secondary.light',
              borderRadius: 1,
            }}
          >
            <Typography 
              variant="body1" 
              sx={{ 
                whiteSpace: 'pre-wrap',
                lineHeight: 1.6,
                fontSize: '14px',
                color: 'text.primary'
              }}
            >
              {element.content}
            </Typography>
          </Box>
        );

      case 'section_header':
        return (
          <Box key={index} sx={{ mb: 3, mt: 2 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: 'secondary.main',
                borderBottom: 2, borderColor: 'secondary.main',
                pb: 1,
                mb: 2,
                fontSize: { xs: '1rem', sm: '1.25rem' }
              }}
            >
              {element.content}
            </Typography>
          </Box>
        );

      default:
        return null;
    }
  }, [artistNumber, artistReviewConfirmed, artistSignatureData, validationErrors, handleArtistSignatureChange]);

  // Calculate if form can be submitted
  const hasArtistNumberElement = artistElements.some(el => el.type === 'artist_number');
  const hasArtistReviewElement = artistElements.some(el => el.type === 'artist_review_checkbox');
  const hasArtistSignatureElement = artistElements.some(el => el.type === 'artist_signature');

  const artistSectionComplete = !hasArtistSection || (
    (!hasArtistNumberElement || artistNumber.trim()) &&
    (!hasArtistReviewElement || artistReviewConfirmed) &&
    (!hasArtistSignatureElement || artistSignatureData)
  );

  const canSubmit = legalCompliance.allComplianceComplete && 
    signatureData && 
    artistSectionComplete;

  const getSubmitButtonText = () => {
    if (submitting) return 'Submitting...';
    if (!legalCompliance.allComplianceComplete) return 'Complete Legal Requirements';
    if (!signatureData) return 'Client Signature Required';
    if (hasArtistSection && !artistSectionComplete) return 'Complete Artist Section';
    return 'Submit Form';
  };

  return (
    // MOBILE FIX: tighter padding on mobile (xs=12px, sm+=24px)
    <Box 
      sx={{ 
        maxWidth: 800, 
        mx: 'auto', 
        p: { xs: 1.5, sm: 3 },
        fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif'
      }}
    >
      {/* Form Header */}
      <Box sx={{ mb: 4, pb: 3, borderBottom: 3, borderColor: 'primary.main' }}>
        {/* MOBILE FIX: scale h4 down on mobile */}
        <Typography 
          variant="h4" 
          sx={{ 
            fontWeight: 700, 
            mb: 1,
            color: 'primary.main',
            fontSize: { xs: '1.5rem', sm: '2.125rem' }
          }}
        >
          {template?.title || 'Custom Consent Form'}
        </Typography>
        {template?.description && (
          <Typography variant="body1" color="text.secondary">
            {template.description}
          </Typography>
        )}
      </Box>

      {/* Client Selection */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonOutlineIcon /> Select Client
        </Typography>
        {loadingClients ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2 }}>
            <CircularProgress size={20} />
            <Typography>Loading clients...</Typography>
          </Box>
        ) : (
          <Autocomplete
            options={clients}
            getOptionLabel={(option) => `${option.first_name} ${option.last_name} (${option.email || 'No email'})`}
            value={selectedClient}
            onChange={(event, newValue) => setSelectedClient(newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search clients..."
                variant="outlined"
                error={!!validationErrors.client}
                helperText={validationErrors.client}
              />
            )}
            isOptionEqualToValue={(option, value) => option.id === value?.id}
          />
        )}
      </Box>

      {/* ID Upload Section */}
      {/* MOBILE FIX: reduced padding on mobile */}
      {template?.enable_id_upload && selectedClient && (
        <Box sx={{ mb: 4, p: { xs: 2, sm: 3 }, bgcolor: 'action.hover', borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom>
            ID Verification
          </Typography>
          
          {selectedClient && (
            <Box sx={{ mb: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={useExistingImage}
                    onChange={handleUseExistingImage}
                    color="primary"
                  />
                }
                label="Use existing ID image on file for this client"
                sx={{ mb: 2, display: 'block' }}
              />
              
              {useExistingImage && (
                <Box sx={{ mb: 2 }}>
                  <Button
                    type="button"
                    variant="outlined"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      fetchClientIdImage();
                    }}
                    disabled={loadingExistingImage}
                    startIcon={loadingExistingImage ? <CircularProgress size={16} /> : null}
                  >
                    {loadingExistingImage ? 'Loading...' : 'Load ID from File'}
                  </Button>
                </Box>
              )}
              
              {existingImageError && (
                <Alert severity="warning" sx={{ mt: 1, mb: 1 }}>
                  {existingImageError}
                </Alert>
              )}
              
              {useExistingImage && clientHasIdImage && secureIdImageSrc && (
                <Box sx={{ mt: 2, p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Client's ID on file:
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <img
                      src={secureIdImageSrc}
                      alt="Client ID"
                      style={{ 
                        maxWidth: '100%', 
                        maxHeight: '300px',
                        objectFit: 'contain',
                        marginBottom: '16px'
                      }}
                    />
                    
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" color="textSecondary" gutterBottom>
                        Please confirm that this ID is accurate and valid for this client.
                      </Typography>
                    
                      {!imageConfirmed ? (
                        <Button
                          type="button"
                          variant="contained"
                          color="primary"
                          startIcon={<CheckCircleIcon />}
                          onClick={(e) => {
                            e.preventDefault();
                            handleConfirmImage();
                          }}
                          sx={{ mt: 1 }}
                        >
                          Confirm ID is Accurate and Valid
                        </Button>
                      ) : (
                        <Alert severity="success" sx={{ mt: 1 }}>
                          ID confirmed as accurate and valid
                        </Alert>
                      )}
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
          )}
          
          {!useExistingImage && (
            <>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                - OR -
              </Typography>
              
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Upload new ID images for this form.
              </Typography>
              
              <Box sx={{ mt: 2 }}>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                  id="id-upload-input"
                />
                <label htmlFor="id-upload-input">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<UploadIcon />}
                  >
                    Select ID Images
                  </Button>
                </label>
                
                {selectedFiles.length > 0 && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {selectedFiles.length} file(s) selected: 
                    {selectedFiles.map(file => ` ${file.name}`).join(', ')}
                  </Typography>
                )}
              </Box>
            </>
          )}

          {validationErrors.id && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {validationErrors.id}
            </Alert>
          )}
        </Box>
      )}

      {/* ==================== CLIENT SECTION ==================== */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="overline" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <PersonOutlineIcon fontSize="small" /> Client Section
        </Typography>
        
        {clientElements.map((element, index) => renderFormElement(element, index))}
      </Box>

      {/* Legal Compliance Section */}
      <Box sx={{ mb: 4 }}>
        <CombinedLegalCompliance
          onComplianceChange={handleLegalComplianceChange}
          apiRef={apiRef}
          disabled={false}
        />
      </Box>

      {/* Client Signature Section */}
      {/* MOBILE FIX: reduced padding on mobile */}
      <Box sx={{ mb: 4, p: { xs: 2, sm: 4 }, border: 2, borderColor: 'divider', borderRadius: 2, bgcolor: 'action.hover' }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonOutlineIcon /> Client Signature
        </Typography>
        <Alert severity="info" sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <SecurityIcon sx={{ mr: 1 }} />
            <Typography variant="body2">
              <strong>Secure E-Signature:</strong> Your signature will be legally binding and protected 
              with industry-standard security measures.
            </Typography>
          </Box>
        </Alert>
        <SignatureCanvas
          onSignatureChange={handleSignatureChange}
          value={signatureData}
          required={true}
          disabled={!legalCompliance.allComplianceComplete}
          error={!!validationErrors.signature}
          helperText={validationErrors.signature || (!legalCompliance.allComplianceComplete ? "Please complete legal requirements above" : "")}
        />
      </Box>

      {/* ==================== ARTIST SECTION ==================== */}
      {artistElements.length > 0 && (
        <Box sx={{ mb: 4 }}>
          {artistElements.map((element, index) => renderArtistElement(element, index))}
        </Box>
      )}

      {/* Status Messages */}
      {submitStatus && (
        <Alert 
          severity={submitStatus.includes('successfully') ? 'success' : 'error'} 
          sx={{ mb: 3 }}
        >
          {submitStatus}
        </Alert>
      )}

      {/* Submit Button */}
      {/* MOBILE FIX: full-width on mobile, auto on sm+ */}
      <Box sx={{ textAlign: 'center', mt: 4 }}>
        <Button
          type="button"
          variant="contained"
          size="large"
          onClick={handleSubmit}
          disabled={submitting || !canSubmit}
          sx={{
            px: { xs: 4, sm: 6 },
            py: 2,
            width: { xs: '100%', sm: 'auto' },
            fontSize: '16px',
            fontWeight: 600,
            background: canSubmit ? 
              'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)' : 
              'linear-gradient(135deg, #ccc 0%, #999 100%)',
            '&:hover': {
              background: canSubmit ? 
                'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)' : 
                'linear-gradient(135deg, #ccc 0%, #999 100%)'
            }
          }}
        >
          {submitting ? (
            <>
              <CircularProgress size={24} sx={{ mr: 1 }} color="inherit" />
              Submitting...
            </>
          ) : (
            getSubmitButtonText()
          )}
        </Button>
      </Box>

      {/* Terms and Conditions Dialog */}
      <Dialog
        open={showTermsDialog}
        onClose={() => setShowTermsDialog(false)}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle>
          <Typography variant="h5" component="span" sx={{ fontWeight: 600 }}>
            {clientTerms.title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Effective Date: {clientTerms.effectiveDate}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {renderTermsContent()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowTermsDialog(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CustomFormRenderer;